import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { getApiUrl } from '../config/api';
import './Present.css';

// Jerarquia por cantidad de digitos:
// 1 digito = Nivel 1 (padre raiz)
// 2 digitos = Nivel 2 (hijo de nivel 1)
// 3 digitos = Nivel 3 (hijo de nivel 2)
// 4 digitos = Nivel 4 (hijo de nivel 3)
// 5 digitos = Nivel 5 (hijo de nivel 4)
// 6 digitos = Nivel 6 (hijo de nivel 5)
// 7 digitos = Nivel 7 (cuentas imputables, hijo de nivel 6)
const getParentCuenta = (cuenta, cuentasMap) => {
    // Buscar el ancestro mas cercano que exista en el mapa
    // Ej: "5111101" -> busca "511110", no existe -> busca "51111", existe -> ese es el padre
    for (let i = cuenta.length - 1; i >= 1; i--) {
        const posiblePadre = cuenta.substring(0, i);
        if (cuentasMap[posiblePadre]) {
            return posiblePadre;
        }
    }
    return null;
};

const getNivelFromLength = (cuenta) => {
    return cuenta.length; // 1 digito = nivel 1, 2 digitos = nivel 2, etc.
};

// Construir arbol a partir de lista plana
const buildTree = (cuentas) => {
    const map = {};
    const roots = [];

    // Ordenar por numero de cuenta
    const sorted = [...cuentas].sort((a, b) => a.cuenta.localeCompare(b.cuenta));

    // Indexar por numero de cuenta
    sorted.forEach(c => {
        map[c.cuenta] = { ...c, hijos: [] };
    });

    // Asignar hijos a padres buscando el ancestro existente mas cercano
    sorted.forEach(c => {
        const parentKey = getParentCuenta(c.cuenta, map);
        if (parentKey) {
            map[parentKey].hijos.push(map[c.cuenta]);
        } else {
            roots.push(map[c.cuenta]);
        }
    });

    return roots;
};

// Verificar si un nodo o sus descendientes coinciden con la busqueda
const nodoCoincide = (nodo, busqueda) => {
    if (!busqueda) return true;
    const term = busqueda.toLowerCase();
    if (nodo.cuenta.toLowerCase().includes(term) || nodo.descripcion.toLowerCase().includes(term)) {
        return true;
    }
    return nodo.hijos.some(hijo => nodoCoincide(hijo, busqueda));
};

// Componente de fila del arbol
function FilaArbol({ nodo, nivel, expandidos, toggleExpandir, onEditar, onEliminar, busqueda }) {
    const tieneHijos = nodo.hijos.length > 0;
    const estaExpandido = expandidos[nodo.cuenta];
    const nivelCuenta = getNivelFromLength(nodo.cuenta);

    // Colores por nivel para diferenciar visualmente
    const coloresNivel = {
        1: '#1a5276',
        2: '#1e8449',
        3: '#b9770e',
        4: '#6c3483',
        5: '#2471a3',
        6: '#a04000',
        7: '#001A70'
    };

    const colorFondo = {
        1: '#eaf2f8',
        2: '#eafaf1',
        3: '#fef9e7',
        4: '#f5eef8',
        5: '#ebf5fb',
        6: '#fdf2e9',
        7: '#ffffff'
    };

    return (
        <>
            <tr style={{ backgroundColor: colorFondo[nivelCuenta] || '#fff' }}>
                <td style={{ paddingLeft: `${nivel * 24 + 12}px`, whiteSpace: 'nowrap' }}>
                    <span
                        className="tree-toggle"
                        onClick={() => tieneHijos && toggleExpandir(nodo.cuenta)}
                        style={{
                            cursor: tieneHijos ? 'pointer' : 'default',
                            display: 'inline-block',
                            width: '20px',
                            textAlign: 'center',
                            marginRight: '6px',
                            fontSize: '12px',
                            color: tieneHijos ? '#555' : '#ccc',
                            userSelect: 'none'
                        }}
                    >
                        {tieneHijos ? (estaExpandido ? '▼' : '▶') : '●'}
                    </span>
                    <span style={{
                        fontWeight: nivelCuenta <= 2 ? '700' : nivelCuenta === 3 ? '600' : '400',
                        color: coloresNivel[nivelCuenta] || '#333',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                    }}>
                        {nodo.cuenta}
                    </span>
                </td>
                <td style={{
                    fontWeight: nivelCuenta <= 2 ? '600' : '400',
                    color: coloresNivel[nivelCuenta] || '#333'
                }}>
                    {nodo.descripcion}
                </td>
                <td style={{ textAlign: 'center' }}>
                    <span className="tree-nivel-badge" style={{
                        backgroundColor: coloresNivel[nivelCuenta] || '#333',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '600'
                    }}>
                        {nodo.nivelcta}
                    </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                    {nodo.imputable === 'S' ? (
                        <span style={{ color: '#27ae60', fontWeight: '600' }}>Si</span>
                    ) : (
                        <span style={{ color: '#95a5a6' }}>No</span>
                    )}
                </td>
                <td style={{ textAlign: 'center' }}>
                    {nodo.ctaActivoFijo === 'S' ? (
                        <span style={{ color: '#27ae60', fontWeight: '600' }}>Si</span>
                    ) : (
                        <span style={{ color: '#95a5a6' }}>No</span>
                    )}
                </td>
                <td>
                    <button
                        className="btn-accion"
                        onClick={() => onEditar(nodo)}
                        title="Editar"
                    >
                        ✏️
                    </button>
                    <button
                        className="btn-accion"
                        onClick={() => onEliminar(nodo.cuenta)}
                        title="Eliminar"
                    >
                        🗑️
                    </button>
                </td>
            </tr>
            {tieneHijos && estaExpandido && nodo.hijos
                .filter(hijo => nodoCoincide(hijo, busqueda))
                .map(hijo => (
                    <FilaArbol
                        key={hijo.cuenta}
                        nodo={hijo}
                        nivel={nivel + 1}
                        expandidos={expandidos}
                        toggleExpandir={toggleExpandir}
                        onEditar={onEditar}
                        onEliminar={onEliminar}
                        busqueda={busqueda}
                    />
                ))
            }
        </>
    );
}

function CGCuenta({ usuario }) {
    const [cuentas, setCuentas] = useState([]);
    const [formulario, setFormulario] = useState({
        cuenta: '',
        descripcion: '',
        nivelcta: '',
        imputable: false,
        ctaactfijo: 'N',
        idOperador: usuario || '',
        fchultmod: null
    });
    const [modoEdicion, setModoEdicion] = useState(false);
    const [mostrarModal, setMostrarModal] = useState(false);
    const [errores, setErrores] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
    const [cuentaAEliminar, setCuentaAEliminar] = useState(null);
    const [expandidos, setExpandidos] = useState({});

    useEffect(() => {
        cargarCuentas();
    }, []);

    const cargarCuentas = async () => {
        try {
            const response = await axios.get(getApiUrl('/api/cuentas'));
            setCuentas(response.data);
        } catch (error) {
            console.error('Error al cargar cuentas:', error);
            toast.error('Error al cargar las cuentas');
        }
    };

    // Construir arbol con memo para evitar recalculos
    const arbol = useMemo(() => buildTree(cuentas), [cuentas]);

    // Contar nodos visibles (filtrados)
    const contarNodos = (nodos, busq) => {
        if (!busq) return cuentas.length;
        let count = 0;
        const contar = (lista) => {
            lista.forEach(n => {
                if (nodoCoincide(n, busq)) {
                    count++;
                    contar(n.hijos);
                }
            });
        };
        contar(nodos);
        return count;
    };

    const toggleExpandir = (cuenta) => {
        setExpandidos(prev => ({ ...prev, [cuenta]: !prev[cuenta] }));
    };

    const expandirTodo = () => {
        const todos = {};
        cuentas.forEach(c => { todos[c.cuenta] = true; });
        setExpandidos(todos);
    };

    const colapsarTodo = () => {
        setExpandidos({});
    };

    // Expandir automaticamente al buscar
    useEffect(() => {
        if (busqueda.trim()) {
            expandirTodo();
        }
    }, [busqueda]);

    const validarFormulario = async () => {
        const nuevosErrores = {};

        if (!formulario.cuenta.trim()) {
            nuevosErrores.cuenta = 'El numero de cuenta es requerido';
        } else if (!/^\d+$/.test(formulario.cuenta.trim())) {
            nuevosErrores.cuenta = 'El numero de cuenta solo puede contener digitos';
        } else if (formulario.cuenta.trim().length > 11) {
            nuevosErrores.cuenta = 'El numero de cuenta no puede tener mas de 11 digitos';
        }

        if (!formulario.descripcion.trim()) {
            nuevosErrores.descripcion = 'La descripcion es requerida';
        } else if (formulario.descripcion.trim().length > 40) {
            nuevosErrores.descripcion = 'La descripcion no puede tener mas de 40 caracteres';
        }

        if (!formulario.nivelcta) {
            nuevosErrores.nivelcta = 'El nivel de cuenta es requerido';
        }

        if (!modoEdicion && !nuevosErrores.cuenta) {
            try {
                const response = await axios.get(getApiUrl(`/api/cuentas/${formulario.cuenta.trim()}`));
                if (response.data) {
                    nuevosErrores.cuenta = 'Ya existe una cuenta con ese numero';
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    nuevosErrores.cuenta = 'Error al verificar duplicados';
                }
            }
        }

        setErrores(nuevosErrores);
        return Object.keys(nuevosErrores).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'cuenta') {
            const numericValue = value.replace(/\D/g, '');
            setFormulario(prev => ({ ...prev, [name]: numericValue }));
        } else if (type === 'checkbox') {
            setFormulario(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormulario(prev => ({ ...prev, [name]: value }));
        }

        if (errores[name]) {
            setErrores(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const esValido = await validarFormulario();
        if (!esValido) return;

        try {
            const datosEnviar = {
                cuenta: formulario.cuenta.trim(),
                descripcion: formulario.descripcion.trim(),
                nivelcta: parseInt(formulario.nivelcta),
                imputable: formulario.imputable ? 'S' : 'N',
                ctaactfijo: formulario.ctaactfijo,
                idOperador: usuario || null,
                fchultmod: new Date().toISOString()
            };

            if (modoEdicion) {
                await axios.put(getApiUrl(`/api/cuentas/${formulario.cuenta}`), datosEnviar);
                toast.success('Cuenta actualizada exitosamente');
            } else {
                await axios.post(getApiUrl('/api/cuentas'), datosEnviar);
                toast.success('Cuenta creada exitosamente');
            }

            cerrarModal();
            cargarCuentas();
        } catch (error) {
            console.error('Error al guardar cuenta:', error);
            toast.error('Error al guardar la cuenta: ' + (error.response?.data?.mensaje || error.message));
        }
    };

    const handleEditar = (cuenta) => {
        setFormulario({
            cuenta: cuenta.cuenta,
            descripcion: cuenta.descripcion,
            nivelcta: cuenta.nivelcta?.toString() || '',
            imputable: cuenta.imputable === 'S',
            ctaactfijo: cuenta.ctaActivoFijo || 'N',
            idOperador: cuenta.idOperador || '',
            fchultmod: cuenta.fchUltMod
        });
        setModoEdicion(true);
        setMostrarModal(true);
        setErrores({});
    };

    const handleEliminar = (cuenta) => {
        setCuentaAEliminar(cuenta);
        setMostrarConfirmacion(true);
    };

    const confirmarEliminar = async () => {
        try {
            await axios.delete(getApiUrl(`/api/cuentas/${cuentaAEliminar}`));
            toast.success('Cuenta eliminada exitosamente');
            cargarCuentas();
        } catch (error) {
            console.error('Error al eliminar cuenta:', error);
            toast.error('Error al eliminar la cuenta');
        } finally {
            setMostrarConfirmacion(false);
            setCuentaAEliminar(null);
        }
    };

    const cancelarEliminar = () => {
        setMostrarConfirmacion(false);
        setCuentaAEliminar(null);
    };

    const abrirModalNuevo = () => {
        setFormulario({
            cuenta: '',
            descripcion: '',
            nivelcta: '',
            imputable: false,
            ctaactfijo: 'N',
            idOperador: usuario || '',
            fchultmod: null
        });
        setModoEdicion(false);
        setMostrarModal(true);
        setErrores({});
    };

    const cerrarModal = () => {
        setMostrarModal(false);
        setFormulario({
            cuenta: '',
            descripcion: '',
            nivelcta: '',
            imputable: false,
            ctaactfijo: 'N',
            idOperador: usuario || '',
            fchultmod: null
        });
        setModoEdicion(false);
        setErrores({});
    };

    const generarPDF = async () => {
        try {
            const response = await axios.get(getApiUrl('/api/reportes/plan-cuentas'), {
                params: { empresa: 'Newton' },
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error al generar reporte:', error);
            toast.error('Error al generar el reporte PDF');
        }
    };

    const exportarExcel = () => {
        const datosExportar = cuentas
            .sort((a, b) => a.cuenta.localeCompare(b.cuenta))
            .map(cuenta => ({
                'Cuenta': cuenta.cuenta,
                'Descripcion': cuenta.descripcion,
                'Nivel': cuenta.nivelcta,
                'Imputable': cuenta.imputable,
                'Cta. Activo Fijo': cuenta.ctaActivoFijo,
                'Operador': cuenta.idOperador,
                'Fecha Modificacion': cuenta.fchUltMod
            }));

        const ws = XLSX.utils.json_to_sheet(datosExportar);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cuentas');
        XLSX.writeFile(wb, 'cuentas_contables.xlsx');
    };

    const arbolFiltrado = busqueda.trim()
        ? arbol.filter(nodo => nodoCoincide(nodo, busqueda))
        : arbol;

    return (
        <div className="present-container">
            <div className="header-seccion">
                <h2>Plan de Cuentas Contable</h2>
                <div className="acciones-header">
                    <input
                        type="text"
                        className="input-busqueda"
                        placeholder="Buscar cuenta..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                    <button className="btn btn-tree-action" onClick={expandirTodo}
                        title="Expandir todo">
                        + Expandir
                    </button>
                    <button className="btn btn-tree-action" onClick={colapsarTodo}
                        title="Colapsar todo">
                        - Colapsar
                    </button>
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo}>
                        ➕ Nueva Cuenta
                    </button>
                    <button className="btn btn-exportar" onClick={generarPDF}>
                        📄 PDF
                    </button>
                    <button className="btn btn-exportar" onClick={exportarExcel}>
                        📊 Excel
                    </button>
                </div>
            </div>

            <div className="tree-legend">
                <span className="tree-legend-item" style={{ color: '#1a5276' }}>■ Nivel 1</span>
                <span className="tree-legend-item" style={{ color: '#1e8449' }}>■ Nivel 2</span>
                <span className="tree-legend-item" style={{ color: '#b9770e' }}>■ Nivel 3</span>
                <span className="tree-legend-item" style={{ color: '#6c3483' }}>■ Nivel 4</span>
                <span className="tree-legend-item" style={{ color: '#2471a3' }}>■ Nivel 5</span>
                <span className="tree-legend-item" style={{ color: '#a04000' }}>■ Nivel 6</span>
                <span className="tree-legend-item" style={{ color: '#001A70' }}>■ Nivel 7 (Imputable)</span>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>Cuenta</th>
                            <th>Descripcion</th>
                            <th style={{ textAlign: 'center' }}>Nivel</th>
                            <th style={{ textAlign: 'center' }}>Imputable</th>
                            <th style={{ textAlign: 'center' }}>Activo Fijo</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {arbolFiltrado.length > 0 ? (
                            arbolFiltrado.map(nodo => (
                                <FilaArbol
                                    key={nodo.cuenta}
                                    nodo={nodo}
                                    nivel={0}
                                    expandidos={expandidos}
                                    toggleExpandir={toggleExpandir}
                                    onEditar={handleEditar}
                                    onEliminar={handleEliminar}
                                    busqueda={busqueda}
                                />
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="sin-datos">
                                    No se encontraron cuentas
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="footer-tabla">
                Total de cuentas: {cuentas.length}
            </div>

            {mostrarConfirmacion && (
                <div className="modal-overlay">
                    <div className="modal-contenido" style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h3>Confirmar eliminacion</h3>
                            <button className="btn-cerrar" onClick={cancelarEliminar}>×</button>
                        </div>
                        <p style={{ padding: '20px' }}>
                            ¿Esta seguro de eliminar la cuenta <strong>{cuentaAEliminar}</strong>?
                        </p>
                        <div className="modal-footer">
                            <button className="btn btn-cancelar" onClick={cancelarEliminar}>
                                Cancelar
                            </button>
                            <button className="btn btn-guardar" onClick={confirmarEliminar}
                                style={{ backgroundColor: '#dc3545' }}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {mostrarModal && (
                <div className="modal-overlay">
                    <div className="modal-contenido">
                        <div className="modal-header">
                            <h3>{modoEdicion ? 'Editar Cuenta' : 'Nueva Cuenta'}</h3>
                            <button className="btn-cerrar" onClick={cerrarModal}>×</button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Numero de Cuenta *</label>
                                <input
                                    type="text"
                                    name="cuenta"
                                    className={`form-input ${errores.cuenta ? 'form-input-error' : ''}`}
                                    value={formulario.cuenta}
                                    onChange={handleChange}
                                    disabled={modoEdicion}
                                    maxLength="11"
                                    placeholder="Solo numeros (max. 11 digitos)"
                                />
                                {errores.cuenta && <span className="form-error">{errores.cuenta}</span>}
                            </div>

                            <div className="form-group">
                                <label>Descripcion *</label>
                                <input
                                    type="text"
                                    name="descripcion"
                                    className={`form-input ${errores.descripcion ? 'form-input-error' : ''}`}
                                    value={formulario.descripcion}
                                    onChange={handleChange}
                                    maxLength="40"
                                />
                                {errores.descripcion && <span className="form-error">{errores.descripcion}</span>}
                            </div>

                            <div className="form-group">
                                <label>Nivel de Cuenta *</label>
                                <select
                                    name="nivelcta"
                                    className={`form-input ${errores.nivelcta ? 'form-input-error' : ''}`}
                                    value={formulario.nivelcta}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione un nivel...</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                    <option value="6">6</option>
                                    <option value="7">7</option>
                                </select>
                                {errores.nivelcta && <span className="form-error">{errores.nivelcta}</span>}
                            </div>

                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        name="imputable"
                                        checked={formulario.imputable}
                                        onChange={handleChange}
                                        style={{ marginRight: '8px' }}
                                    />
                                    Cuenta Imputable
                                </label>
                            </div>

                            <div className="form-group">
                                <label>ID Operador (Usuario logueado)</label>
                                <input
                                    type="text"
                                    name="idOperador"
                                    className="form-input"
                                    value={formulario.idOperador}
                                    onChange={handleChange}
                                    disabled
                                    title="Este campo se establece automaticamente con el usuario logueado"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn btn-cancelar" onClick={cerrarModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-guardar">
                                    {modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CGCuenta;
